import React, { useState } from 'react';
import { Container, Row, Col } from 'styled-bootstrap-grid';
import styled from 'styled-components';
import { FiLayers } from 'react-icons/fi';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import {
  Play,
  Heart,
  MessageSquare,
  BarChart,
  Edit2,
  Award,
} from 'react-feather';
import { style, Track, useIsCurrentUser, useIsReleaseTrack } from '../../api';
import { Link } from '../common/Miscellanea';
import RoundPlayButton from '../common/Buttons/V2/RoundPlayButton';

import { CenteredLoader } from '../common/Loader';
import {
  TextBold22,
  TextRegular12,
  TextRegular14,
  TextBold12,
  TextBold14,
  TextBold18,
} from '../common/Text';
import GenreLabel from '../common/GenreLabel';
import { ProfileLink as ProfileLinkUnstyled } from '../common/Link';
import ShareButton from '../common/Buttons/V2/ShareButton';
import SharingModal from '../Modals/SharingModal';
import LikeTrackButton from '../common/Buttons/V2/LikeTrackButton';
import {
  NoDesktop,
  Desktop,
  noDesktop,
  desktop,
  useIsMobile,
} from '../../style/constants';
import Waveform from '../Waveform';
import { TrackSubmittedContestsList } from '../TrackSubmittedContestsList';
import DistributionMessage from '../../VuexyDesign/organisms/DistributionMessage';
import { Button, LikeButton } from '../../VuexyDesign/atoms/_index';
import ReleasedTrackInfo from './ReleasedTrackInfo';
import CentralizeButton from '../../VuexyDesign/atoms/CentralizeButton';

const TrackInfo = ({ track }: { track: Track }) => {
  const { t } = useTranslation('tracks');
  const [showSharingModal, setShowSharingModal] = useState(false);
  const isMobile = useIsMobile();
  const isTrackReleased = useIsReleaseTrack(track?.release_status);
  const isCurrentUser = useIsCurrentUser(track?.user.id);
  const trackId = track?.id;

  if (!track) {
    return <CenteredLoader />;
  }

  if (isTrackReleased) {
    return (
      <StyledContainer>
        <ReleasedTrackInfo track={track} />
      </StyledContainer>
    );
  }

  const {
    title,
    track_desc,
    genre,
    user,
    total_plays,
    total_likes,
    total_comments,
  } = track;

  return (
    <StyledContainer>
      <Row>
        <NoDesktop>
          {genre && (
            <Genre className="d-flex w-100 text-lowercase justify-content-center">
              {track.charts &&
                track.charts.current_week &&
                track.charts.current_week.genre.position <= 100 && (
                  <div id="position" className="d-flex align-items-center">
                    <Award size={16} className="text-info" />
                    <TextRegular12>
                      <Trans
                        i18nKey="tracks:positionInCharts"
                        components={{ TextBold12: <TextBold12 /> }}
                        values={{
                          position: track.charts.current_week.genre.position,
                          genre: genre.name,
                        }}
                      />
                    </TextRegular12>
                  </div>
                )}
              <GenreLabel genreId={genre.id} />
            </Genre>
          )}
          <DistributionMessage track={track} />
        </NoDesktop>
        <Col lg={8} noGutter={isMobile} className="d-flex align-items-center">
          <NoDesktop>
            <div className="text-white mr-1">
              <RoundPlayButton size="70px" />
            </div>
          </NoDesktop>
          <div>
            <div>
              <InnerRow style={{ marginBottom: 4 }}>
                <TrackInfoTitle>{title}</TrackInfoTitle>
              </InnerRow>
              <TextBold18 style={{ opacity: 0.7 }}>
                <ProfileLink profileId={user.id}>
                  {user.display_name}
                </ProfileLink>
              </TextBold18>
            </div>
            <NoDesktop>
              <PlaysLikes className="d-flex mt-1">
                <Plays className="flex-row" plays={total_plays} />
                <Likes className="flex-row" likes={total_likes} />
                <Comments className="flex-row" comments={total_comments} />
              </PlaysLikes>
            </NoDesktop>
          </div>
        </Col>
        <Desktop>
          <Col lg={4} className="p-0">
            <div className="d-flex text-lowercase w-100 justify-content-end">
              {genre && (
                <Genre className="d-flex text-lowercase">
                  {track.charts &&
                    track.charts.current_week &&
                    track.charts.current_week.genre.position <= 100 && (
                      <div id="position" className="d-flex align-items-center">
                        <Award size={16} className="text-info" />
                        <TextRegular12>
                          <Trans
                            i18nKey="tracks:positionInCharts"
                            components={{ TextBold12: <TextBold12 /> }}
                            values={{
                              position:
                                track.charts.current_week.genre.position,
                              genre: genre.name,
                            }}
                          />
                        </TextRegular12>
                      </div>
                    )}
                  <GenreLabel genreId={genre.id} />
                </Genre>
              )}
            </div>
          </Col>
        </Desktop>
      </Row>
      <PlaysLikesRow className="d-flex flex-row justify-content-between">
        <Desktop>
          <PlaysLikes className="d-flex">
            <Plays className="flex-row" plays={total_plays} />
            <Likes className="flex-row" likes={total_likes} />
            <Comments className="flex-row" comments={total_comments} />
          </PlaysLikes>
        </Desktop>
        <Desktop>
          <LikeShareRow>
            <LikeButton trackId={trackId} />
            {isCurrentUser && (
              <>
                <Link
                  href={`/statistics?tab=demos&track_id=${track.id}`}
                  passHref
                >
                  <SecondaryButton
                    outline
                    className="br-5 d-flex"
                    color="secondary"
                  >
                    <BarChart size={14} color={style.dark} />
                    <TextRegular14 className="text-dark align-middle">
                      {t('tracks:stats') as string}
                    </TextRegular14>
                  </SecondaryButton>
                </Link>
                <Link href={`/edit-track/${trackId}`} passHref>
                  <SecondaryButton
                    outline
                    className="br-5 d-flex"
                    color="secondary"
                  >
                    <Edit2 size={14} color={style.dark} />
                    <TextRegular14 className="text-dark align-middle">
                      {t('tracks:edit') as string}
                    </TextRegular14>
                  </SecondaryButton>
                </Link>
              </>
            )}
            <ShareButton
              className="text-dark"
              onClick={() => setShowSharingModal(true)}
            />
          </LikeShareRow>
        </Desktop>
      </PlaysLikesRow>
      <NoDesktop>
        <StyledWaveform className="position-relative" trackId={trackId} />
      </NoDesktop>
      <NoDesktop>
        <LikeShareRow className="mb-1">
          <LikeButton trackId={trackId} />
          {isCurrentUser && (
            <>
              <Link
                href={`/statistics?tab=demos&track_id=${track.id}`}
                passHref
              >
                <SecondaryButton
                  outline
                  className="br-5 d-flex"
                  color="secondary"
                >
                  <BarChart size={14} color={style.dark} />
                  <TextRegular14 className="text-dark align-middle">
                    {t('tracks:stats') as string}
                  </TextRegular14>
                </SecondaryButton>
              </Link>
              <Link href={`/edit-track/${trackId}`} passHref>
                <SecondaryButton
                  outline
                  className="br-5 d-flex"
                  color="secondary"
                >
                  <Edit2 size={14} color={style.dark} />
                  <TextRegular12 className="text-dark align-middle">
                    {t('tracks:edit') as string}
                  </TextRegular12>
                </SecondaryButton>
              </Link>
            </>
          )}
          <ShareButton
            className="text-dark"
            onClick={() => setShowSharingModal(true)}
          />
        </LikeShareRow>
      </NoDesktop>
      <Desktop>
        <div className="text-white d-flex align-items-center">
          <RoundPlayButton size="60px" />
          <StyledWaveform trackId={trackId} />
        </div>
      </Desktop>
      <Row>
        <Col
          lg={9}
          sm={12}
          noGutter={isMobile}
          style={isMobile ? { marginLeft: 15, marginRight: 15 } : {}}
        >
          <Row className="w-100 m-0 mb-50">
            <Desktop>
              <DistributionMessage track={track} />
            </Desktop>
          </Row>
          <TrackSubmittedContestsList
            style={{ marginBottom: 10 }}
            contestIds={track.contests}
          />
          <Description>{track_desc}</Description>
        </Col>
        <Desktop>
          <Col lg={3} className="mx-auto p-1 d-flex align-items-center">
            <SimilarTrackLink
              href={{ pathname: '/explore', query: { genre: genre?.id } }}
            >
              <div>
                <CentralizeButton
                  style={{ width: '110%' }}
                  text={t('tracks:exploreSimilarTracks') as string}
                  outline
                  color="dark"
                  className="br-5 d-flex "
                  icon={FiLayers}
                  iconSize="20"
                  iconColor={style.dark}
                  isIconLeftSide
                  size="sm"
                />
              </div>
            </SimilarTrackLink>
          </Col>
        </Desktop>
      </Row>
      <SharingModal
        type="tracks"
        id={track.id}
        show={showSharingModal}
        close={() => setShowSharingModal(false)}
      />
    </StyledContainer>
  );
};

export default TrackInfo;

const SecondaryButton = styled(Button)`
  padding: 8px 12px;
  gap: 10px;
  white-space: nowrap;
`;

const TrackInfoTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #000;
`;

const StyledWaveform = styled(Waveform)`
  display: flex;
  align-items: center;
  justify-content: center;
  /* width: 100%; */
  @media ${noDesktop} {
    margin-left: 0px !important;
  }
`;
const Genre = styled.div`
  gap: 16px;
  margin: 33px 0px;
  #position {
    gap: 4px;
  }
`;

const StyledContainer = styled(Container)`
  ${Waveform} {
    height: 70px;
    margin: 0px 15px;

    @media ${noDesktop} {
      margin-bottom: 20px;
    }
  }
`;

const InnerRow = styled.div`
  display: flex;

  @media ${noDesktop} {
    flex-direction: column;

    ${GenreLabel} {
      width: fit-content;
      margin-bottom: 6px;
    }

    ${TextBold22} {
      margin-bottom: 10px;
    }
  }

  @media ${desktop} {
    ${GenreLabel} {
      margin-left: 8px;
    }
  }
`;

const ProfileLink = styled(ProfileLinkUnstyled)`
  color: ${style.black};
  font-weight: bold;
`;

const Description = styled(TextRegular12)`
  color: ${style.grey_dark_new};

  @media ${noDesktop} {
    margin-top: 28px;
  }
`;

const PlaysLikesWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlaysLikesText = styled(TextRegular12)`
  color: ${style.grey_main_new};
  display: flex;
  align-items: center;
  height: 16px;
`;

const PlaysLikes = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Plays = styled(
  ({ plays, className }: { plays: number; className?: string }) => (
    <PlaysLikesWrapper className={className}>
      <PlaysLikesText>
        <Play
          size={16}
          color={style.grey_main_new}
          style={{ marginRight: '8px' }}
        />
        <TextBold14 as="div">{plays}</TextBold14>
      </PlaysLikesText>
    </PlaysLikesWrapper>
  ),
)``;

const Comments = styled(
  ({ comments, className }: { comments: number; className?: string }) => (
    <PlaysLikesWrapper className={className}>
      <PlaysLikesText>
        <MessageSquare
          size={16}
          color={style.grey_main_new}
          style={{ marginRight: '8px' }}
        />
        <TextBold14 as="div">{comments}</TextBold14>
      </PlaysLikesText>
    </PlaysLikesWrapper>
  ),
)``;

const PlaysLikesRow = styled(Row)`
  @media ${desktop} {
    padding-left: 15px;
  }
  padding-top: 11px;
  padding-bottom: 22px;

  @media ${noDesktop} {
    ${LikeTrackButton} {
      margin-left: auto;
    }
  }
`;

const Likes = styled(
  ({ likes, className }: { likes: number; className?: string }) => (
    <PlaysLikesWrapper className={className}>
      <PlaysLikesText>
        <Heart
          size={16}
          color={style.grey_main_new}
          style={{ marginRight: '8px' }}
        />
        <TextBold14 as="div">{likes}</TextBold14>
      </PlaysLikesText>
    </PlaysLikesWrapper>
  ),
)``;

const SimilarTrackLink = styled(Link)`
  @media ${noDesktop} {
    margin-top: 28px;
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const LikeShareRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 7px;
`;
